import { apiClient } from '@/lib/api-client';
import i18n from '@/i18n';
import type { paths } from '@/api/schema';

type StartPath = paths['/customers/{customer}/limit-check']['post'];
export type LimitCheckBody = NonNullable<StartPath['requestBody']>['content']['application/json'];
export type LimitCheckAnswer = paths['/customers/{customer}/limit-checks/{limitCheck}']['get']['responses'][200]['content']['application/json'];

export const LIMIT_POLL_INTERVAL_MS = 3000;
/** The backend gives up on the bank after 180 s; a little longer here so its verdict reaches us first. */
export const LIMIT_POLL_TIMEOUT_MS = 190_000;

const RUNNING = new Set(['pending', 'scoring']);

/** T224: the bank scored the customer but could not give a limit (timeout, unavailable, refused the request). */
export class LimitCheckError extends Error {
  readonly code: string;

  constructor(code: string | null | undefined) {
    super(limitCheckErrorMessage(code));
    this.name = 'LimitCheckError';
    this.code = code ?? '';
  }
}

export function limitCheckErrorMessage(code: string | null | undefined): string {
  const key = `loan_limit.errors.${code ?? ''}`;
  return i18n.exists(key) ? i18n.t(key) : i18n.t('loan_limit.errors.default');
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException('Aborted', 'AbortError'));
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });
}

/**
 * T224: asks the bank for the customer's limit. The bank scores asynchronously, so the backend answers 202 and the
 * status is polled every few seconds until it is final. Resolves with an approved or rejected answer (a rejection is
 * an answer: the limit screen shows why) and throws {@link LimitCheckError} when the bank could not give one.
 */
export async function requestLimit(
  customerId: number,
  body: LimitCheckBody,
  options: { intervalMs?: number; timeoutMs?: number; signal?: AbortSignal } = {},
): Promise<LimitCheckAnswer> {
  const { intervalMs = LIMIT_POLL_INTERVAL_MS, timeoutMs = LIMIT_POLL_TIMEOUT_MS, signal } = options;

  const started = await apiClient.POST('/customers/{customer}/limit-check', {
    params: { path: { customer: customerId } },
    body,
    signal,
  });
  if (started.error) throw started.error;

  let answer: LimitCheckAnswer = started.data;
  const startedAt = Date.now();

  while (RUNNING.has(answer.status)) {
    if (Date.now() - startedAt > timeoutMs) throw new LimitCheckError('BANK_TIMEOUT');
    await sleep(intervalMs, signal);

    try {
      const next = await apiClient.GET('/customers/{customer}/limit-checks/{limitCheck}', {
        params: {
          path: { customer: customerId, limitCheck: answer.id },
          query: { basket_amount: body.basket_amount, 'product_ids[]': body.product_ids },
        },
        signal,
      });
      if (next.error) {
        // A blip on our side or the network: ask again until the time is up. Anything else is final.
        if (next.response.status < 500) throw next.error;
        continue;
      }
      answer = next.data;
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      if (e instanceof TypeError) continue; // fetch: network error
      throw e;
    }
  }

  if (answer.status === 'failed') throw new LimitCheckError(answer.error_code);

  return answer;
}
